/**
 * StaffPanel — Retire / restore scheduling staff (Users admin → Staff tab).
 *
 * Retiring hides the staff member from the outreach Calendar pills, the
 * Rewards + Expenses team strips, the Stats cards and the booking dropdown
 * WITHOUT deleting any of their history — restoring brings everything back
 * exactly as it was. Staff themselves are provisioned automatically when a
 * user with a staff name is created (see the Users tab).
 *
 * Auth: exchanges the parent badge admin token for a pp token, same as
 * UsersPanel. The retire/restore endpoint only accepts master sessions.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, UserMinus, UserPlus, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StaffPanel({ badgeToken }) {
  const [ppToken, setPpToken] = useState(null);
  const [exchangeError, setExchangeError] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyName, setBusyName] = useState(null);

  // Exchange badge admin token for a pp admin token
  useEffect(() => {
    if (!badgeToken) return;
    (async () => {
      try {
        const res = await fetch(`${API}/pp/auth/exchange-badge-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ badge_token: badgeToken }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          setExchangeError(b.detail || "Session exchange failed");
          return;
        }
        const d = await res.json();
        setPpToken(d.token);
      } catch (_) {
        setExchangeError("Network error during session exchange");
      }
    })();
  }, [badgeToken]);

  const load = useCallback(async () => {
    if (!ppToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/consultations/staff-teams?include_retired=1`, {
        headers: { "X-Auth-Token": ppToken },
      });
      if (!res.ok) throw new Error("load failed");
      const d = await res.json();
      setItems(Array.isArray(d?.staff) ? d.staff : []);
    } catch {
      toast.error("Couldn't load the staff list");
    } finally {
      setLoading(false);
    }
  }, [ppToken]);

  useEffect(() => { load(); }, [load]);

  const setActive = async (s, active) => {
    if (busyName) return;
    setBusyName(s.full);
    try {
      const res = await fetch(`${API}/consultations/staff-teams/${encodeURIComponent(s.full)}/active`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Auth-Token": ppToken },
        body: JSON.stringify({ active }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "save failed");
      toast.success(active
        ? `${s.full} is back — Calendar, teams and Stats restored.`
        : `${s.full} retired — hidden from Calendar, teams and Stats. History kept.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Couldn't update the staff member");
    } finally {
      setBusyName(null);
    }
  };

  // The month a staff member's sections start appearing (Stats cards,
  // Calendar pill, Rewards/Expenses team, booking dropdown). Empty = show in
  // all history. New staff default to the month they were created.
  const setStartMonth = async (s, startMonth) => {
    if (busyName) return;
    setBusyName(s.full);
    try {
      const res = await fetch(`${API}/consultations/staff-teams/${encodeURIComponent(s.full)}/start-month`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Auth-Token": ppToken },
        body: JSON.stringify({ start_month: startMonth }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "save failed");
      toast.success(startMonth
        ? `${s.full} appears from ${startMonth} onward in every section.`
        : `${s.full} now shows in all history.`);
      await load();
    } catch (e) {
      toast.error(e?.message || "Couldn't set the start month");
    } finally {
      setBusyName(null);
    }
  };

  if (exchangeError) {
    return <p className="text-sm text-red-600" data-testid="staff-exchange-error">{exchangeError}</p>;
  }

  return (
    <div data-testid="staff-panel">
      <p className="text-xs text-slate-500 mb-4 max-w-2xl">
        Staff appear here automatically when a user with a staff name is created on the Users tab.
        Retiring hides someone from the Calendar, Rewards, Expenses, Stats and the booking dropdown —
        all of their past records stay intact, and <b>Bring back</b> restores everything instantly.
      </p>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Calendar label</th>
              <th className="text-left px-4 py-3 font-semibold">Team</th>
              <th className="text-left px-4 py-3 font-semibold">Sections start</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="staff-list">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No staff yet.</td></tr>
            )}
            {!loading && items.map((s) => {
              const active = s.active !== false;
              return (
                <tr key={s.full} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`staff-row-${s.short}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{s.full}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.short}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.team_label || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <input
                        type="month"
                        value={s.start_month || ""}
                        disabled={busyName === s.full}
                        onChange={(e) => setStartMonth(s, e.target.value)}
                        data-testid={`staff-start-month-${s.short}`}
                        title="First month this person appears in Stats, Calendar, Rewards, Expenses and the booking dropdown"
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                      />
                      {s.start_month ? (
                        <button
                          type="button"
                          onClick={() => setStartMonth(s, "")}
                          disabled={busyName === s.full}
                          data-testid={`staff-start-month-clear-${s.short}`}
                          title="Clear — show in all history"
                          className="text-[11px] font-semibold text-slate-400 hover:text-slate-700 underline"
                        >
                          All history
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">All history</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {active ? (
                      <span className="text-xs text-emerald-700" data-testid={`staff-status-${s.short}`}>active</span>
                    ) : (
                      <span className="text-xs text-slate-400" data-testid={`staff-status-${s.short}`}>retired</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyName === s.full}
                      onClick={() => setActive(s, !active)}
                      data-testid={`staff-${active ? "retire" : "restore"}-${s.short}`}
                    >
                      {busyName === s.full ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : active ? (
                        <span className="inline-flex items-center gap-1 text-red-600">
                          <UserMinus className="w-3.5 h-3.5" /> Retire
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <UserPlus className="w-3.5 h-3.5" /> Bring back
                        </span>
                      )}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5 max-w-2xl">
        <UsersIcon className="w-3.5 h-3.5 shrink-0" />
        A retired staff member's login (if they have one) stays active — deactivate it from the Users tab if needed.
      </p>
    </div>
  );
}
